import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ProductGrid } from './product-grid';
const PRODUCT_HTML_MARKER = /<!--PRODUCTS:([\s\S]*?)-->/;
const PRODUCT_SENTINEL = /\[\[PRODUCTS:([\s\S]*?)\]\]/;
// Fallback for malformed markers (e.g., <!--PRODUCTS:...]] or <!--PRODUCTS:...] without proper closing)
const PRODUCT_MALFORMED_MARKER = /<!--PRODUCTS:(\[[\s\S]*?\])\]*/;
const CURRENCY_REGEX = /(?:SAR|AED|USD|EUR|GBP|KWD|OMR|QAR|BHD|AUD|CAD|[$€£₹])\s?[\d.,]+|[\d.,]+\s?(?:SAR|AED|USD|EUR|GBP|KWD|OMR|QAR|BHD|AUD|CAD)/i;
const IMAGE_REGEX = /(https?:\/\/[^\s)]+\.(?:png|jpe?g|gif|webp|avif))/i;
const PRODUCT_LINK_REGEX = /\[([^\]]*)\]\((https?:\/\/[^\s)]+\/products\/[^\s)]+)\)/gi;
const MEANINGLESS_LABEL_REGEX = /^(?:here|this|click here|view(?: it| here)?|tap here|open|link|view it here)$/i;
const sanitizeLine = (line) => line.replace(/[\*_`~]+/g, '').trim();
const splitLabel = (label) => {
    const sanitized = label.replace(/\s+/g, ' ').trim();
    const delimiters = [' — ', ' – ', ' - '];
    for (const delim of delimiters) {
        const idx = sanitized.lastIndexOf(delim);
        if (idx > 0) {
            const potentialPrice = sanitized.slice(idx + delim.length).trim();
            if (CURRENCY_REGEX.test(potentialPrice)) {
                return {
                    name: sanitized.slice(0, idx).trim(),
                    price: potentialPrice,
                };
            }
        }
    }
    return { name: sanitized };
};
const CONFIRMATION_VERB_REGEX = /\b(add(?:ed)?|cart|basket|wishlist|order(?:ed)?)\b/i;
const cleanupResidualText = (text, names, urls) => {
    const productLinkPattern = /\[([^\]]*)\]\((https?:\/\/[^\s)]+\/products\/[^\s)]+)\)/i;
    const productImagePattern = /^!\[[^\]]*]\((https?:\/\/[^\s)]+)\)$/i;
    const lines = text
        .split(/\r?\n/)
        .map((line) => sanitizeLine(line))
        .filter((line) => {
        if (!line)
            return false;
        if (line === '!')
            return false;
        if (/^\d+\.?$/.test(line))
            return false;
        if (/^https?:\/\//i.test(line))
            return false;
        if (/^view product$/i.test(line))
            return false;
        if (/^[a-z0-9]{1,4}$/i.test(line))
            return false;
        if (/^[-*•]\s*$/.test(line))
            return false;
        const isNumbered = /^\d+\.\s*/.test(line);
        if (isNumbered) {
            const remainder = line.replace(/^\d+\.\s*/, '').trim();
            const hasConfirmationIntent = CONFIRMATION_VERB_REGEX.test(remainder);
            const matchesKnownName = names.some((name) => {
                if (!name)
                    return false;
                const cleanedName = name.toLowerCase();
                const cleanedRemainder = remainder.toLowerCase();
                return (cleanedRemainder === cleanedName ||
                    cleanedRemainder.startsWith(`${cleanedName} `) ||
                    cleanedRemainder.endsWith(` ${cleanedName}`));
            });
            const looksLikeProductLine = matchesKnownName ||
                CURRENCY_REGEX.test(remainder) ||
                productLinkPattern.test(remainder) ||
                productImagePattern.test(remainder) ||
                urls.some((url) => remainder.includes(url));
            if (!hasConfirmationIntent && looksLikeProductLine) {
                return false;
            }
        }
        const isBulleted = /^[-*•]\s+/.test(line);
        if (isBulleted) {
            const remainder = line.replace(/^[-*•]\s+/, '').trim();
            if (!CONFIRMATION_VERB_REGEX.test(remainder)) {
                const looksLikeProductLine = CURRENCY_REGEX.test(remainder) ||
                    productLinkPattern.test(remainder) ||
                    productImagePattern.test(remainder) ||
                    names.some((name) => name && remainder.toLowerCase().startsWith(name.toLowerCase())) ||
                    urls.some((url) => remainder.includes(url));
                if (looksLikeProductLine) {
                    return false;
                }
            }
        }
        if (productLinkPattern.test(line))
            return false;
        if (productImagePattern.test(line))
            return false;
        if (names.some((name) => name && line.toLowerCase() === name.toLowerCase())) {
            return false;
        }
        if (urls.some((url) => line.includes(url))) {
            return false;
        }
        return true;
    });
    return lines.join('\n').trim();
};
const hasMeaningfulContent = (value) => {
    if (!value)
        return false;
    // Consider only letters/digits as meaningful; bare punctuation like "." should be treated as empty.
    return /[A-Za-z0-9]/.test(value);
};
const isCartConfirmation = (value) => {
    if (!hasMeaningfulContent(value))
        return false;
    const lower = value.toLowerCase();
    // Check for cart/basket keywords (English and Arabic)
    const hasCartKeyword = lower.includes('cart') ||
        lower.includes('basket') ||
        lower.includes('السلة') || // Arabic: cart/basket
        lower.includes('سلة'); // Arabic: basket (short form)
    if (!hasCartKeyword)
        return false;
    // Strong confirmation patterns (English)
    if (/\badd(?:ed)?\b/.test(lower))
        return true;
    if (lower.includes('in your cart'))
        return true;
    if (lower.includes('now in your cart'))
        return true;
    if (lower.includes('added to your'))
        return true;
    if (lower.includes('to your cart'))
        return true;
    if (lower.includes('to your basket'))
        return true;
    if (lower.includes('successfully added'))
        return true;
    if (lower.includes('has been added'))
        return true;
    if (lower.includes('as well'))
        return true; // "added to your cart as well"
    if (lower.includes('you can view it'))
        return true; // Common cart confirmation phrase
    // Arabic confirmation patterns
    if (lower.includes('تمت الإضافة'))
        return true; // "was added"
    if (lower.includes('تم الإضافة'))
        return true; // "has been added"
    if (lower.includes('تم إضافة'))
        return true; // "was added" (alternative)
    if (lower.includes('أضفت'))
        return true; // "I added"
    if (lower.includes('إلى السلة'))
        return true; // "to the cart"
    return false;
};
/**
 * Checks if a product name looks like a cart confirmation message rather than an actual product name.
 * This prevents rendering product badges when the LLM incorrectly includes confirmation text as product names.
 */
const isProductNameConfirmationLike = (name) => {
    if (!name)
        return false;
    const lower = name.toLowerCase().trim();
    // Match patterns like "Product Name added to cart", "Product Name has been added", etc.
    if (/\b(added|add)\b.*\b(cart|basket|السلة)/i.test(lower))
        return true;
    if (/\b(has been|have been|successfully)\b.*\b(added|add)/i.test(lower))
        return true;
    if (/\b(now in|in your|to your)\b.*\b(cart|basket|السلة)/i.test(lower))
        return true;
    // Arabic patterns in product names
    if (lower.includes('تمت الإضافة') || lower.includes('تم الإضافة'))
        return true;
    if (lower.includes('أضفت') && lower.includes('السلة'))
        return true;
    if (lower.includes('إلى السلة'))
        return true; // "to the cart"
    return false;
};
const ensurePrice = (price) => {
    const val = (price !== null && price !== void 0 ? price : '').trim();
    if (!val)
        return '';
    return val;
};
const deriveNameFromContext = (raw, index, slugFallback) => {
    const upToMatch = raw.slice(0, index);
    const lines = upToMatch.split(/\r?\n/);
    for (let i = lines.length - 1; i >= 0; i -= 1) {
        const candidate = sanitizeLine(lines[i]);
        if (!candidate)
            continue;
        if (/view product/i.test(candidate))
            continue;
        if (/^https?:\/\//i.test(candidate))
            continue;
        if (/^here are/i.test(candidate))
            continue;
        if (candidate === '!')
            continue;
        if (/^would you like/i.test(candidate))
            break;
        return candidate;
    }
    return slugFallback;
};
const parseJsonProducts = (raw, marker) => {
    const match = raw.match(marker);
    if (!match)
        return null;
    try {
        const products = JSON.parse(match[1]);
        if (!Array.isArray(products) || products.length === 0) {
            return null;
        }
        const rawText = raw.replace(marker, '').trim();
        const cleanedText = cleanupResidualText(rawText, products.map((p) => sanitizeLine(p.name || '')), products.map((p) => p.url || ''));
        let finalText = cleanedText.trim();
        // If scrubbing removes everything or leaves only punctuation (e.g. a lone "."),
        // avoid rendering junk; first try the original text, then synthesize a simple
        // confirmation from the first product name.
        if (!hasMeaningfulContent(finalText)) {
            const rawFallback = rawText.trim();
            if (hasMeaningfulContent(rawFallback)) {
                finalText = rawFallback;
            }
            else {
                const first = Array.isArray(products) && products.length > 0 ? products[0] : null;
                const name = first ? sanitizeLine(first.name || '') : '';
                if (hasMeaningfulContent(name)) {
                    finalText = `Here\u2019s ${name}.`;
                }
                else {
                    finalText = '';
                }
            }
        }
        return { text: finalText, products };
    }
    catch (error) {
        console.warn('Failed to parse product payload:', error);
        return null;
    }
};
const parseProductsFromMarkdown = (raw) => {
    var _a;
    const matches = Array.from(raw.matchAll(PRODUCT_LINK_REGEX));
    if (matches.length === 0) {
        return null;
    }
    const results = [];
    const seenUrls = new Set();
    const textSegments = [];
    let cursor = 0;
    for (const match of matches) {
        const [full, label = '', url = ''] = match;
        const start = (_a = match.index) !== null && _a !== void 0 ? _a : 0;
        if (!url || seenUrls.has(url)) {
            continue;
        }
        const slug = (() => {
            const slugMatch = url.match(/\/products\/([^/?#]+)/i);
            return slugMatch ? decodeURIComponent(slugMatch[1]) : url;
        })();
        const preceding = raw.slice(cursor, start);
        if (preceding.trim()) {
            textSegments.push(preceding);
        }
        const lookbackWindow = raw.slice(Math.max(0, start - 250), start);
        const lookaheadWindow = raw.slice(start + full.length, start + full.length + 250);
        const { name: labelName, price: labelPrice } = splitLabel(label);
        let name = sanitizeLine(labelName);
        if (!name || /view product/i.test(name) || MEANINGLESS_LABEL_REGEX.test(name)) {
            name = sanitizeLine(deriveNameFromContext(raw, start, slug.replace(/[-_]+/g, ' ')));
        }
        if (!name) {
            name = slug.replace(/[-_]+/g, ' ').trim();
        }
        let price = labelPrice ? ensurePrice(labelPrice) : '';
        const priceMatchAfter = lookaheadWindow.match(CURRENCY_REGEX);
        const priceMatchBefore = lookbackWindow.match(CURRENCY_REGEX);
        if (!price) {
            if (priceMatchAfter && priceMatchAfter[0]) {
                price = ensurePrice(priceMatchAfter[0]);
            }
            else if (priceMatchBefore && priceMatchBefore[0]) {
                price = ensurePrice(priceMatchBefore[0]);
            }
        }
        let image;
        const lookbackImage = lookbackWindow.match(IMAGE_REGEX);
        if (lookbackImage && lookbackImage[0]) {
            image = lookbackImage[0];
        }
        else {
            const lookaheadImage = lookaheadWindow.match(IMAGE_REGEX);
            if (lookaheadImage && lookaheadImage[0]) {
                image = lookaheadImage[0];
            }
        }
        const id = url || slug;
        results.push({
            id,
            name,
            price: ensurePrice(price),
            image,
            url,
        });
        seenUrls.add(url);
        cursor = start + full.length;
    }
    const trailing = raw.slice(cursor);
    if (trailing.trim()) {
        textSegments.push(trailing);
    }
    if (results.length === 0) {
        return null;
    }
    const names = results.map((p) => sanitizeLine(p.name));
    const urls = results.map((p) => { var _a; return (_a = p.url) !== null && _a !== void 0 ? _a : ''; });
    let cleanedText = cleanupResidualText(textSegments.join('\n'), names, urls).trim();
    if (!hasMeaningfulContent(cleanedText)) {
        cleanedText = '';
    }
    return {
        text: cleanedText,
        products: results,
    };
};
const ProductMessage = ({ text, products, }) => {
    return (_jsxs("div", { className: "flex flex-col gap-3", children: [text && (_jsx("div", { className: "max-w-[85%] rounded-2xl rounded-bl-md bg-gray-100 px-4 py-2.5 text-[14px] leading-relaxed text-gray-900", children: _jsx("span", { className: "whitespace-pre-wrap", children: text }) })), _jsx(ProductGrid, { products: products })] }));
};
export const productFormatter = (text) => {
    const raw = String(text || '');
    // Extract text outside of product markers for analysis
    const textWithoutMarkers = raw
        .replace(/<!--PRODUCTS:[\s\S]*?-->/g, '')
        .replace(/\[\[PRODUCTS:[\s\S]*?\]\]/g, '')
        .replace(/<!--PRODUCTS:\[[\s\S]*?\]\]*/g, '') // Malformed marker fallback
        .trim();
    // LAYER 1: Strong cart/basket guard with Arabic support
    // If ANY cart/basket keywords found ANYWHERE, be very defensive
    const lowerRaw = raw.toLowerCase();
    const lowerText = textWithoutMarkers.toLowerCase();
    const hasCartKeywordAnywhere = lowerRaw.includes('cart') ||
        lowerRaw.includes('basket') ||
        lowerRaw.includes('السلة') || // Arabic: cart/basket
        lowerRaw.includes('سلة'); // Arabic: basket
    // If cart keywords found, check if this is a confirmation
    if (hasCartKeywordAnywhere) {
        // Check if the text outside markers is a cart confirmation
        if (isCartConfirmation(lowerText) || isCartConfirmation(lowerRaw)) {
            // Strip ALL product markers and return only text
            return textWithoutMarkers || raw.replace(/<!--PRODUCTS:[\s\S]*?-->/g, '').replace(/\[\[PRODUCTS:[\s\S]*?\]\]/g, '').replace(/<!--PRODUCTS:\[[\s\S]*?\]\]*/g, '').trim();
        }
        // Additional guard: if there's very little text outside markers (< 15 chars),
        // and cart keywords exist, likely a malformed cart confirmation
        if (textWithoutMarkers.length > 0 && textWithoutMarkers.length < 15) {
            return textWithoutMarkers;
        }
    }
    // LAYER 2: Filter out products with confirmation-like names
    const maybeRender = (parsed) => {
        if (!parsed)
            return null;
        // Filter out products with confirmation-like names
        const validProducts = parsed.products.filter(p => !isProductNameConfirmationLike(p.name));
        // If all products were filtered out, just return the text
        if (validProducts.length === 0) {
            return parsed.text || raw;
        }
        // Check if remaining text is a cart confirmation
        if (isCartConfirmation(parsed.text)) {
            return parsed.text || raw;
        }
        return _jsx(ProductMessage, { text: parsed.text, products: validProducts });
    };
    const parsedFromHtml = parseJsonProducts(raw, PRODUCT_HTML_MARKER);
    const htmlNode = maybeRender(parsedFromHtml);
    if (htmlNode !== null) {
        return htmlNode;
    }
    const parsedFromSentinel = parseJsonProducts(raw, PRODUCT_SENTINEL);
    const sentinelNode = maybeRender(parsedFromSentinel);
    if (sentinelNode !== null) {
        return sentinelNode;
    }
    // Try malformed marker fallback (e.g., <!--PRODUCTS:...]] without proper --> closing)
    const parsedFromMalformed = parseJsonProducts(raw, PRODUCT_MALFORMED_MARKER);
    const malformedNode = maybeRender(parsedFromMalformed);
    if (malformedNode !== null) {
        return malformedNode;
    }
    // Only try markdown parsing if this is NOT a cart/basket confirmation message
    // This prevents "[here](product-url)" links in cart confirmations from rendering as product cards
    const isConfirmationMessage = isCartConfirmation(textWithoutMarkers) || isCartConfirmation(raw);
    if (!isConfirmationMessage) {
        const parsedFallback = parseProductsFromMarkdown(raw);
        const fallbackNode = maybeRender(parsedFallback);
        if (fallbackNode !== null) {
            return fallbackNode;
        }
    }
    return text;
};
/**
 * Combines multiple formatters into a single formatter
 * Tries each formatter in sequence - if one transforms the text, returns that result
 * Otherwise, returns the original text
 */
export const composeFormatters = (...formatters) => {
    return (text) => {
        const textStr = String(text || '');
        // Try each formatter in sequence
        for (const formatter of formatters) {
            const result = formatter(textStr);
            // If formatter transformed the text (returned something different), use it
            if (result !== textStr) {
                return result;
            }
        }
        // No formatter matched, return original text
        return text;
    };
};
/**
 * Formats Zid order/invoice links into a clean "View Order" button.
 * Matches patterns like [here](https://xxx.zid.store/o/...) or raw URLs.
 */
export const orderLinkFormatter = (text) => {
    const raw = String(text || '');
    // Match markdown links with Zid order URLs: [text](url)
    const MARKDOWN_ZID_ORDER = /\[([^\]]*)\]\((https?:\/\/[^\s]*\.zid\.store\/o\/[^\s)]+)\)/g;
    // Check if there are any Zid order links (markdown format only for safety)
    if (!MARKDOWN_ZID_ORDER.test(raw)) {
        return text;
    }
    // Reset regex
    MARKDOWN_ZID_ORDER.lastIndex = 0;
    // Build React nodes by splitting around markdown links
    const nodes = [];
    let lastIndex = 0;
    let match;
    let keyIndex = 0;
    while ((match = MARKDOWN_ZID_ORDER.exec(raw)) !== null) {
        const [fullMatch, , url] = match;
        const matchStart = match.index;
        // Add text before the link
        if (matchStart > lastIndex) {
            nodes.push(_jsx("span", { children: raw.slice(lastIndex, matchStart) }, `text-${keyIndex++}`));
        }
        // Add the View Order button
        const href = url.replace(/[)\]\}.,;]+$/, '');
        nodes.push(_jsxs("a", { href: href, target: "_blank", rel: "noreferrer", className: "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white", style: { background: 'linear-gradient(to right, #10b981, #059669)', cursor: 'pointer' }, children: [_jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }), _jsx("polyline", { points: "14 2 14 8 20 8" }), _jsx("line", { x1: "16", y1: "13", x2: "8", y2: "13" }), _jsx("line", { x1: "16", y1: "17", x2: "8", y2: "17" }), _jsx("polyline", { points: "10 9 9 9 8 9" })] }), "View Order"] }, `order-${keyIndex++}`));
        lastIndex = matchStart + fullMatch.length;
    }
    // If no links were processed, return original
    if (nodes.length === 0) {
        return text;
    }
    // Add remaining text after the last link
    if (lastIndex < raw.length) {
        nodes.push(_jsx("span", { children: raw.slice(lastIndex) }, `text-${keyIndex++}`));
    }
    return _jsx(_Fragment, { children: nodes });
};
/**
 * Formats Calendly links into a clean "Book Now" button.
 * Strips surrounding punctuation/labels like "(…)", ")", and "[Book Now]" before links.
 */
export const calendlyFormatter = (text) => {
    const GLOBAL_LINK_RE = /(https?:\/\/[^\s]*calendly\.com[^\s]*)/gi;
    const LINK_RE = /^https?:\/\/[^\s]*calendly\.com[^\s]*$/i;
    const ONLY_LINK_WITH_LABEL = /^\s*[\(\[]?\s*(?:\[?\s*Book\s+Now\s*\]?\:?\s*)?(https?:\/\/[^\s]*calendly\.com[^\s]*)\s*[\)\]]?\.?\s*$/i;
    const raw = String(text || '');
    // If the entire message is just a (labeled) Calendly link, render only the button
    const only = raw.match(ONLY_LINK_WITH_LABEL);
    if (only) {
        const href = only[1].replace(/^[([\s]+/, '').replace(/[)\]\}.,;]+$/, '');
        return (_jsx("a", { href: href, target: "_blank", rel: "noreferrer", className: "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium text-white", style: { background: 'linear-gradient(to right, #9333ea, #7e22ce)', cursor: 'pointer' }, children: "Book Now" }));
    }
    // Otherwise, replace in-text links with a button and drop nearby stray punctuation/labels
    const scrubbed = raw.replace(/\s*\[?\s*Book\s+Now\s*\]?\:?\s*(?=(https?:\/\/[^\s]*calendly\.com))/gi, ' ');
    const parts = scrubbed.split(GLOBAL_LINK_RE);
    if (parts.length === 1)
        return text;
    const nodes = [];
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const isLink = LINK_RE.test(part.trim());
        if (isLink) {
            const href = part.replace(/^[([\s]+/, '').replace(/[)\]\}.,;]+$/, '');
            nodes.push(_jsx("a", { href: href, target: "_blank", rel: "noreferrer", className: "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium text-white", style: { background: 'linear-gradient(to right, #9333ea, #7e22ce)', cursor: 'pointer' }, children: "Book Now" }, i));
            continue;
        }
        const prev = i > 0 ? parts[i - 1] : '';
        const next = i + 1 < parts.length ? parts[i + 1] : '';
        const prevIsLink = LINK_RE.test((prev || '').trim());
        const nextIsLink = LINK_RE.test((next || '').trim());
        const trimmed = part.trim();
        // Drop pure punctuation around link, e.g., wrapping parentheses and trailing dots
        if ((prevIsLink || nextIsLink) && /^[()\[\]\s.]+$/.test(trimmed)) {
            continue;
        }
        // Drop stray label tokens adjacent to link
        if (nextIsLink && /^\[?\s*Book\s+Now\s*\]?\:?$/i.test(trimmed)) {
            continue;
        }
        nodes.push(_jsx("span", { children: part }, i));
    }
    return _jsx(_Fragment, { children: nodes });
};
/**
 * General markdown link formatter that converts [text](url) to clickable links.
 * This handles links that aren't caught by product or Calendly formatters.
 */
export const markdownLinkFormatter = (text) => {
    const raw = String(text || '');
    // Regex to match markdown links: [text](url)
    // Excludes product links (handled by productFormatter) and Calendly links (handled by calendlyFormatter)
    const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    // Check if there are any markdown links
    const hasLinks = MARKDOWN_LINK_REGEX.test(raw);
    if (!hasLinks) {
        return text;
    }
    // Reset regex after test
    MARKDOWN_LINK_REGEX.lastIndex = 0;
    // Split the text and replace links with React elements
    const parts = [];
    let lastIndex = 0;
    let match;
    let keyIndex = 0;
    while ((match = MARKDOWN_LINK_REGEX.exec(raw)) !== null) {
        const [fullMatch, linkText, url] = match;
        const matchStart = match.index;
        // Skip if this is a product link (already handled by productFormatter)
        if (url.includes('/products/')) {
            continue;
        }
        // Skip if this is a Calendly link (already handled by calendlyFormatter)
        if (url.toLowerCase().includes('calendly.com')) {
            continue;
        }
        // Add text before the link
        if (matchStart > lastIndex) {
            parts.push(_jsx("span", { children: raw.slice(lastIndex, matchStart) }, `text-${keyIndex++}`));
        }
        // Add the clickable link
        parts.push(_jsx("a", { href: url, target: "_blank", rel: "noopener noreferrer", className: "text-primary underline hover:text-primary/80 transition-colors", children: linkText }, `link-${keyIndex++}`));
        lastIndex = matchStart + fullMatch.length;
    }
    // If no links were actually processed (all were product/calendly links), return original
    if (parts.length === 0) {
        return text;
    }
    // Add remaining text after the last link
    if (lastIndex < raw.length) {
        parts.push(_jsx("span", { children: raw.slice(lastIndex) }, `text-${keyIndex++}`));
    }
    return _jsx(_Fragment, { children: parts });
};
/**
 * Finds the latest Calendly link in a list of messages, scanning from newest to oldest.
 * Only considers agent (non-local) messages.
 */
export const findLatestCalendlyLink = (messages) => {
    var _a, _b;
    const REG = /(https?:\/\/[^\s]*calendly\.com[^\s]*)/i;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        const m = messages[i];
        const isAgent = !((_b = (_a = m.from) === null || _a === void 0 ? void 0 : _a.isLocal) !== null && _b !== void 0 ? _b : false);
        if (!isAgent)
            continue;
        const match = String(m.message || '').match(REG);
        if (match && match[1]) {
            const clean = match[1].replace(/^[([\s]+/, '').replace(/[)\]\}.,;]+$/, '');
            return clean;
        }
    }
    return null;
};
